import type { TicketStatusFilter, TicketSummary } from '@nadoutong/contracts';
import { TICKET_STATUSES } from '@nadoutong/contracts';
import { ChevronDown, ChevronRight, Paperclip } from 'lucide-react';
import { Fragment, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTicketsList } from '../api/useTickets.ts';
import { formatTimestamp } from '../components/formatters.ts';
import { PanelError } from '../components/PanelError.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { useT } from '../i18n/index.ts';
import { Button, Chip, EmptyState, PageHeader, Pagination, Panel, cx } from '../ui/index.ts';
import { CreateTicketForm } from './tickets/CreateTicketForm.tsx';
import { TicketReplyThread } from './tickets/TicketReplyThread.tsx';
import {
  TICKET_STATUS_CHIP_TONES,
  TICKET_STATUS_LABEL_KEYS,
  TICKET_TYPE_LABEL_KEYS,
  describeTicketLoadError,
  parseTicketPage,
  parseTicketStatusFilter,
} from './tickets/ticketMeta.ts';

/**
 * `/tickets` — support tickets (tenant #116, website side of the
 * desktop governance panel).
 *
 * Status filter and page live in the URL (`?status=`, `?page=`) so a
 * filtered view is a pasteable link, same shape as `/requests`. Writes
 * go through `patchParams` with `replace` and collapse the expanded
 * row — a filter change reshapes the result set, and an open thread
 * from the old set means nothing in the new one.
 *
 * The list is server-filtered (`?status=`) and server-paged (`total`
 * from the response). Visibility is role-scoped by the API: members see
 * their own tickets, owner/admin the whole org — differing counts per
 * role are contract behavior the UI never flags.
 */

const PAGE_SIZE = 20;

const STATUS_FILTERS: readonly TicketStatusFilter[] = ['all', ...TICKET_STATUSES];

export function Tickets(): JSX.Element {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = parseTicketStatusFilter(searchParams.get('status'));
  const page = parseTicketPage(searchParams.get('page'));
  const offset = (page - 1) * PAGE_SIZE;
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const ticketsQ = useTicketsList({ status, limit: PAGE_SIZE, offset });
  const tickets = ticketsQ.data?.tickets ?? [];
  const total = ticketsQ.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /**
   * Single write path for the URL state. Defaults leave no trace
   * (`status=all` and `page=1` delete their params), and every write
   * collapses the expanded thread.
   */
  function patchParams(patch: Readonly<Record<string, string | null>>): void {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value.length === 0) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        }
        return next;
      },
      { replace: true },
    );
    setExpandedId(null);
  }

  function selectStatus(next: TicketStatusFilter): void {
    patchParams({ status: next === 'all' ? null : next, page: null });
  }

  function selectPage(next: number): void {
    patchParams({ page: next <= 1 ? null : String(next) });
  }

  return (
    <section data-testid="tickets-page" className="flex flex-col gap-4">
      <PageHeader title={t('tickets.title')} sub={t('tickets.subtitle')} testId="tickets-header" />

      <CreateTicketForm />

      <Panel caption={t('tickets.list.caption')} padded testId="tickets-list">
        <div className="flex flex-col gap-4">
          <div
            role="radiogroup"
            aria-label={t('tickets.list.filter_aria')}
            data-testid="tickets-filter"
            className="inline-flex w-fit flex-wrap items-center gap-1 rounded-lg border border-line bg-card p-1"
          >
            {STATUS_FILTERS.map((filter) => {
              const isActive = filter === status;
              return (
                <button
                  key={filter}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  data-testid={`tickets-filter-${filter}`}
                  onClick={() => selectStatus(filter)}
                  className={cx(
                    'inline-flex items-center justify-center rounded px-3 py-1 text-label transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40',
                    isActive ? 'bg-forest text-on-forest' : 'text-ink-mid hover:text-ink',
                  )}
                >
                  {t(TICKET_STATUS_LABEL_KEYS[filter])}
                </button>
              );
            })}
          </div>

          {ticketsQ.isLoading ? (
            <div className="flex flex-col gap-2" data-testid="tickets-loading">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-12" />
              ))}
              <span className="sr-only">{t('tickets.list.loading')}</span>
            </div>
          ) : ticketsQ.isError ? (
            <PanelError
              message={describeTicketLoadError(ticketsQ.error, t)}
              retryLabel={t('tickets.list.retry')}
              onRetry={() => {
                void ticketsQ.refetch();
              }}
              testId="tickets-error"
            />
          ) : tickets.length === 0 && status !== 'all' ? (
            <EmptyState
              testId="tickets-empty-filtered"
              title={t('tickets.list.empty_filtered.title')}
              hint={t('tickets.list.empty_filtered.hint')}
              action={
                <Button variant="secondary" onClick={() => selectStatus('all')}>
                  {t('tickets.status.all')}
                </Button>
              }
            />
          ) : tickets.length === 0 ? (
            <EmptyState
              testId="tickets-empty"
              title={t('tickets.list.empty.title')}
              hint={t('tickets.list.empty.hint')}
            />
          ) : (
            <>
              <div className="flex flex-col rounded-lg border border-line">
                {tickets.map((ticket, index) => (
                  <Fragment key={ticket.id}>
                    <TicketRow
                      ticket={ticket}
                      first={index === 0}
                      expanded={expandedId === ticket.id}
                      onToggle={() =>
                        setExpandedId((current) => (current === ticket.id ? null : ticket.id))
                      }
                    />
                    {expandedId === ticket.id ? (
                      <TicketReplyThread
                        ticketId={ticket.id}
                        status={ticket.status}
                        attachmentCount={ticket.attachmentCount}
                      />
                    ) : null}
                  </Fragment>
                ))}
              </div>
              <footer className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-caption text-ink-low" data-testid="tickets-page-summary">
                  {t('tickets.list.pagination.summary', {
                    from: offset + 1,
                    to: Math.min(offset + PAGE_SIZE, total),
                    total,
                  })}
                </span>
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={selectPage}
                  prevLabel={t('tickets.list.pagination.prev')}
                  nextLabel={t('tickets.list.pagination.next')}
                  ariaLabel={t('tickets.list.pagination.aria')}
                  testId="tickets-pagination"
                />
              </footer>
            </>
          )}
        </div>
      </Panel>
    </section>
  );
}

function TicketRow({
  ticket,
  first,
  expanded,
  onToggle,
}: {
  readonly ticket: TicketSummary;
  readonly first: boolean;
  readonly expanded: boolean;
  readonly onToggle: () => void;
}): JSX.Element {
  const t = useT();
  const firstLine = ticket.description.split(/\r?\n/, 1)[0] ?? '';
  return (
    <button
      type="button"
      aria-expanded={expanded}
      data-testid={`tickets-row-${ticket.id}`}
      onClick={onToggle}
      className={cx(
        'flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors duration-150 hover:bg-active/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40',
        !first && 'border-t border-line-soft',
      )}
    >
      <span className="flex w-full flex-wrap items-center gap-x-3 gap-y-1">
        {expanded ? (
          <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-ink-low" />
        ) : (
          <ChevronRight aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-ink-low" />
        )}
        <Chip tone="neutral" lamp={false} testId={`tickets-type-${ticket.id}`}>
          {t(TICKET_TYPE_LABEL_KEYS[ticket.type])}
        </Chip>
        <span className="min-w-0 flex-1 truncate text-body text-ink" title={ticket.description}>
          {firstLine}
        </span>
        <Chip tone={TICKET_STATUS_CHIP_TONES[ticket.status]} testId={`tickets-status-${ticket.id}`}>
          {t(TICKET_STATUS_LABEL_KEYS[ticket.status])}
        </Chip>
      </span>
      <span className="flex items-center gap-2 pl-[26px] font-mono text-mono-label text-ink-low">
        <span className="tabular-nums">{formatTimestamp(ticket.createdAt)}</span>
        {ticket.attachmentCount > 0 ? (
          <span className="inline-flex items-center gap-1">
            <Paperclip aria-hidden="true" className="h-3 w-3" />
            {t('tickets.list.attachments', { count: ticket.attachmentCount })}
          </span>
        ) : null}
      </span>
    </button>
  );
}
